import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/store";
import { setCookie } from "../../../helpers/cookie";

const PREFERRED_LANGUAGE_COOKIE = "preferred_language";

/**
 * Main hook for language functionality.
 * Handles the logic for switching languages by navigating to the correct URL.
 */
export function useLanguage() {
  const {
    currentLanguage,
    supportedLanguages,
    languageNames,
    defaultLanguage,
  } = useSelector((state: RootState) => state.language);

  /**
   * Switches the language by navigating to the URL with the appropriate `?lang` parameter.
   * This ensures the URL reflects the current language state.
   */
  const switchLanguage = useCallback(
    (lang: string) => {
      // Prevent navigation if the language is not supported or is already active.
      if (!supportedLanguages.includes(lang) || lang === currentLanguage) {
        return;
      }

      // Set the cookie immediately. This ensures the preference is saved
      // even if the user navigates away before the next page loads.
      setCookie(PREFERRED_LANGUAGE_COOKIE, lang, 365);

      const currentUrl = new URL(window.location.href);

      // If the user is switching to the default language, remove the 'lang' parameter
      // to keep the URL clean. Otherwise, set it to the selected language.
      if (lang === defaultLanguage) {
        currentUrl.searchParams.delete("lang");
      } else {
        currentUrl.searchParams.set("lang", lang);
      }

      // Navigate to the newly constructed URL.
      window.location.href = currentUrl.toString();
    },
    [currentLanguage, supportedLanguages, defaultLanguage]
  );

  return {
    currentLanguage,
    supportedLanguages,
    languageNames,
    switchLanguage,
  };
}
